/**
 * Image Service Layer
 *
 * A staff engineer would create this service to:
 * - Centralize image loading logic
 * - Provide caching mechanisms
 * - Handle image optimization
 * - Abstract image source management
 * - Enable future extensibility (CDN, lazy loading, etc.)
 */

import { createImageElement } from "../utils/imageUtils";

class ImageService {
	constructor() {
		this.cache = new Map();
		this.loadingPromises = new Map();
		this.basePath = "/src/assets/images/customer_photos/";
		this.fallbackImage = null; // Could be a placeholder image
	}

	/**
	 * Get image URL with caching and error handling
	 * @param {string} filename - The image filename
	 * @param {Object} options - Loading options
	 * @returns {Promise<string>} Image URL
	 */
	async getImage(filename, options = {}) {
		const { useCache = true, timeout = 10000, quality = "original" } = options;

		// Return cached image if available
		if (useCache && this.cache.has(filename)) {
			return this.cache.get(filename);
		}

		// Return existing loading promise if in progress
		if (this.loadingPromises.has(filename)) {
			return this.loadingPromises.get(filename);
		}

		// Create new loading promise
		const loadingPromise = this._loadImage(filename, { timeout, quality });
		this.loadingPromises.set(filename, loadingPromise);

		try {
			const imageUrl = await loadingPromise;

			// Cache successful loads
			if (useCache) {
				this.cache.set(filename, imageUrl);
			}

			return imageUrl;
		} catch (error) {
			console.error(`Failed to load image ${filename}:`, error);
			return this.fallbackImage;
		} finally {
			// Clean up loading promise
			this.loadingPromises.delete(filename);
		}
	}

	/**
	 * Internal method to load a single image
	 * @private
	 */
	async _loadImage(filename, { timeout }) {
		return new Promise((resolve, reject) => {
			const timeoutId = setTimeout(() => {
				reject(new Error(`Image loading timeout: ${filename}`));
			}, timeout);

			const loadImageAsync = async () => {
				try {
					// Dynamic import for code splitting
					const imageModule = await import(
						/* webpackChunkName: "gallery-images" */
						`../assets/images/customer_photos/${filename}`
					);

					const imageUrl = imageModule.default;

					// Preload to ensure image is actually available
					const img = createImageElement();
					img.onload = () => {
						clearTimeout(timeoutId);
						resolve(imageUrl);
					};
					img.onerror = () => {
						clearTimeout(timeoutId);
						reject(new Error(`Failed to load image: ${filename}`));
					};
					img.src = imageUrl;
				} catch (importError) {
					clearTimeout(timeoutId);
					reject(
						new Error(
							`Failed to import image: ${filename} - ${importError.message}`,
						),
					);
				}
			};

			loadImageAsync();
		});
	}

	/**
	 * Batch load multiple images with controlled concurrency
	 * @param {string[]} filenames - Array of image filenames
	 * @param {Object} options - Loading options
	 * @returns {Promise<Map>} Map of filename to URL/error
	 */
	async getImages(filenames, options = {}) {
		const { concurrency = 3, failFast = false, ...imageOptions } = options;

		const results = new Map();
		const chunks = this._chunkArray(filenames, concurrency);

		for (const chunk of chunks) {
			const promises = chunk.map(async (filename) => {
				try {
					const url = await this.getImage(filename, imageOptions);
					return { filename, url, success: true };
				} catch (error) {
					const result = { filename, error, success: false };
					if (failFast) throw error;
					return result;
				}
			});

			const chunkResults = await Promise.allSettled(promises);

			chunkResults.forEach((result) => {
				if (result.status === "fulfilled") {
					const { filename, url, error, success } = result.value;
					results.set(filename, success ? url : error);
				} else if (failFast) {
					throw result.reason;
				}
			});
		}

		return results;
	}

	/**
	 * Preload images for better UX
	 * @param {string[]} filenames - Images to preload
	 * @param {Object} options - Preload options
	 */
	async preloadImages(filenames, options = {}) {
		const { concurrency = 2 } = options;

		// Use lower concurrency for preloading to not block critical resources
		const preloadOptions = {
			...options,
			concurrency,
			useCache: true,
		};

		try {
			await this.getImages(filenames, preloadOptions);
		} catch (error) {
			console.warn("Some images failed to preload:", error);
		}
	}

	/**
	 * Get image metadata with URL
	 * @param {Object} imageData - Image metadata object
	 * @param {Object} options - Loading options
	 */
	async getImageWithMetadata(imageData, options = {}) {
		try {
			const url = await this.getImage(imageData.filename, options);
			return {
				...imageData,
				src: url,
				isLoaded: true,
				error: null,
			};
		} catch (error) {
			return {
				...imageData,
				src: this.fallbackImage,
				isLoaded: false,
				error: error.message,
			};
		}
	}

	/**
	 * Batch process image metadata with URLs
	 * @param {Object[]} imageDataArray - Array of image metadata
	 * @param {Object} options - Loading options
	 */
	async getImagesWithMetadata(imageDataArray, options = {}) {
		const { concurrency = 3 } = options;
		const results = [];
		const chunks = this._chunkArray(imageDataArray, concurrency);

		for (const chunk of chunks) {
			const promises = chunk.map((imageData) =>
				this.getImageWithMetadata(imageData, options),
			);

			const chunkResults = await Promise.allSettled(promises);
			const processedChunk = chunkResults.map((result) =>
				result.status === "fulfilled"
					? result.value
					: {
							...chunk[chunkResults.indexOf(result)],
							src: this.fallbackImage,
							isLoaded: false,
							error: result.reason?.message || "Unknown error",
						},
			);

			results.push(...processedChunk);
		}

		return results;
	}

	/**
	 * Clear cache
	 * @param {string|string[]} filenames - Specific files to clear, or all if not provided
	 */
	clearCache(filenames = null) {
		if (!filenames) {
			this.cache.clear();
			return;
		}

		const files = Array.isArray(filenames) ? filenames : [filenames];
		files.forEach((filename) => this.cache.delete(filename));
	}

	/**
	 * Get cache statistics
	 */
	getCacheStats() {
		return {
			size: this.cache.size,
			entries: Array.from(this.cache.keys()),
			loadingCount: this.loadingPromises.size,
			currentlyLoading: Array.from(this.loadingPromises.keys()),
		};
	}

	/**
	 * Set fallback image for failed loads
	 * @param {string} fallbackUrl - URL or path to fallback image
	 */
	setFallback(fallbackUrl) {
		this.fallbackImage = fallbackUrl;
	}

	/**
	 * Utility method to chunk arrays
	 * @private
	 */
	_chunkArray(array, chunkSize) {
		const chunks = [];
		for (let i = 0; i < array.length; i += chunkSize) {
			chunks.push(array.slice(i, i + chunkSize));
		}
		return chunks;
	}

	/**
	 * Generate responsive image URLs (for future CDN integration)
	 * @param {string} filename - Base filename
	 * @param {Object} options - Responsive options
	 */
	getResponsiveImageUrls(filename, options = {}) {
		const {
			sizes = [320, 640, 1024, 1920],
			format = "webp",
			fallbackFormat = "png",
		} = options;

		// For now, return the original image
		// In the future, this could integrate with a CDN or image processing service
		const baseUrl = this.basePath + filename;

		return {
			srcSet: sizes.map((size) => `${baseUrl} ${size}w`).join(", "),
			sizes: "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw",
			src: baseUrl, // fallback
			format,
			fallbackFormat,
		};
	}
}

// Create singleton instance
const imageService = new ImageService();

// Set up default fallback image (you can create a placeholder)
// imageService.setFallback('/src/assets/images/placeholder.png');

export default imageService;

// Named exports for specific use cases
export const {
	getImage,
	getImages,
	preloadImages,
	getImageWithMetadata,
	getImagesWithMetadata,
	clearCache,
	getCacheStats,
	setFallback,
	getResponsiveImageUrls,
} = imageService;
